/**
 * Workspace Security - Core security functions for workspace isolation and contamination prevention
 */

import { supabase } from '@/integrations/supabase/client';
import { secureLog, safeLog } from './authorizationUtils';
import type { WorkspaceValidation, AIProcessingRequest, SecurityViolation } from '../types';

/**
 * CRITICAL: Validate workspace ownership before any AI processing
 */
export async function validateWorkspaceOwnership(
  userId: string,
  workspaceId: string
): Promise<WorkspaceValidation> {
  try {
    const { data: workspace, error } = await supabase
      .from('workspaces')
      .select('id, name, user_id')
      .eq('id', workspaceId)
      .eq('user_id', userId)
      .single();

    if (error || !workspace) {
      return {
        isValid: false,
        workspaceId,
        ownerId: 'unknown',
        violations: ['Workspace not found or access denied'],
        securityLevel: 'critical'
      };
    }

    return {
      isValid: true,
      workspaceId: workspace.id,
      ownerId: workspace.user_id,
      violations: [],
      securityLevel: 'safe'
    };
  } catch (error) {
    await secureLog('error', '[WorkspaceSecurity] Validation error:', error);
    return {
      isValid: false,
      workspaceId,
      ownerId: 'error',
      violations: ['Validation error occurred'],
      securityLevel: 'critical'
    };
  }
}

/**
 * Create isolated AI processing context
 */
export function createIsolatedAIContext(
  userId: string,
  workspaceId: string,
  workspaceName: string
): AIProcessingRequest {
  const sessionId = `${userId}-${workspaceId}-${Date.now()}`;
  const processingQueue = `ai-${workspaceId}-${Date.now()}`;
  const validationToken = generateValidationToken(userId, workspaceId);

  return {
    requestId: `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    userId,
    workspaceId,
    workspaceName,
    timestamp: new Date(),
    isolatedContext: {
      sessionId,
      processingQueue,
      validationToken
    }
  };
}

/**
 * Generate validation token for request integrity
 */
function generateValidationToken(userId: string, workspaceId: string): string {
  const timestamp = Date.now();
  const data = `${userId}:${workspaceId}:${timestamp}`;
  return btoa(data);
}

/**
 * Validate AI processing response before database insertion
 */
export async function validateAIResponse(
  response: any[],
  originalRequest: AIProcessingRequest,
  expectedContentType: 'jewelry' | 'weight-loss' | 'general'
): Promise<{
  isValid: boolean;
  validRecords: any[];
  rejectedRecords: any[];
  violations: string[];
}> {
  const violations: string[] = [];
  const validRecords: any[] = [];
  const rejectedRecords: any[] = [];

  // 1. Validate request integrity
  const tokenValid = validateToken(originalRequest.isolatedContext.validationToken, originalRequest.userId, originalRequest.workspaceId);
  if (!tokenValid) {
    violations.push('Invalid validation token - possible request tampering');
    return { isValid: false, validRecords: [], rejectedRecords: response, violations };
  }

  // 2. Validate workspace ownership
  const workspaceValidation = await validateWorkspaceOwnership(originalRequest.userId, originalRequest.workspaceId);
  if (!workspaceValidation.isValid) {
    violations.push(...workspaceValidation.violations);
    return { isValid: false, validRecords: [], rejectedRecords: response, violations };
  }

  // 3. Content-based validation
  for (const record of response) {
    const contentValid = validateContentForWorkspace(record, expectedContentType, originalRequest.workspaceId);
    
    if (contentValid.isValid) {
      validRecords.push({
        ...record,
        user_id: originalRequest.userId,
        workspace_id: originalRequest.workspaceId,
        validation_metadata: {
          processedAt: new Date().toISOString(),
          requestId: originalRequest.requestId,
          sessionId: originalRequest.isolatedContext.sessionId
        }
      });
    } else {
      rejectedRecords.push(record);
      violations.push(...contentValid.violations);
    }
  }

  return {
    isValid: violations.length === 0,
    validRecords,
    rejectedRecords,
    violations
  };
}

/**
 * Validate token integrity
 */
function validateToken(token: string, userId: string, workspaceId: string): boolean {
  try {
    const decoded = atob(token);
    const [tokenUserId, tokenWorkspaceId, timestamp] = decoded.split(':');
    
    if (tokenUserId !== userId || tokenWorkspaceId !== workspaceId) {
      return false;
    }
    
    const tokenTime = parseInt(timestamp);
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    
    return (now - tokenTime) < oneHour;
  } catch {
    return false;
  }
}

/**
 * Content validation for specific workspace types
 */
function validateContentForWorkspace(
  record: any,
  expectedType: 'jewelry' | 'weight-loss' | 'general',
  workspaceId: string
): { isValid: boolean; violations: string[] } {
  const violations: string[] = [];
  
  const textContent = [
    record.cluster_name,
    record.title,
    record.primary_keyword,
    record.secondary_keywords,
    record.description,
    record.content,
    record.keyword_text
  ].filter(Boolean).join(' ').toLowerCase();

  const weightLossTerms = [
    'weight loss', 'diet', 'fitness', 'fat loss', 'calories', 'exercise',
    'metabolism', 'nutrition', 'supplements', 'gym', 'muscle', 'health'
  ];

  const jewelryTerms = [
    'jewelry', 'jewellery', 'terracotta', 'ornament', 'necklace', 'earring',
    'bracelet', 'ring', 'pendant', 'gold', 'silver', 'gemstone'
  ];

  // Specific validation for terracotta jewelry workspace
  if (workspaceId === '6cf120c4-1d18-4c6b-8f4b-6de27877c2e9') {
    const hasWeightLossContent = weightLossTerms.some(term => textContent.includes(term));
    if (hasWeightLossContent) {
      violations.push(`Weight loss content detected in jewelry workspace: ${textContent.slice(0, 100)}...`);
    }
  }

  // Validation for weight loss workspaces
  const weightLossWorkspaces = ['ddbb213d-8574-46ef-ba4a-42a6f9acd500', '4b4648c8-8bc7-40a2-bc71-6bcf53aecc9e'];
  if (weightLossWorkspaces.includes(workspaceId)) {
    const hasJewelryContent = jewelryTerms.some(term => textContent.includes(term));
    if (hasJewelryContent) {
      violations.push(`Jewelry content detected in weight loss workspace: ${textContent.slice(0, 100)}...`);
    }
  }

  return {
    isValid: violations.length === 0,
    violations
  };
}

/**
 * Log security violations for monitoring
 */
export async function logSecurityViolation(
  violation: SecurityViolation
): Promise<void> {
  try {
    await supabase
      .from('workspace_violation_logs')
      .insert({
        table_name: 'ai_processing',
        attempted_user_id: violation.userId,
        attempted_workspace_id: violation.workspaceId,
        violation_type: violation.violationType,
        violation_details: violation.details
      });
  } catch (error) {
    await secureLog('error', '[WorkspaceSecurity] Failed to log violation:', error);
  }
}

/**
 * Enhanced AI analysis webhook with workspace isolation
 */
export function createSecureAIWebhookPayload(
  userId: string,
  workspaceId: string,
  workspaceName: string,
  keywordData: any[]
) {
  const isolatedContext = createIsolatedAIContext(userId, workspaceId, workspaceName);
  
  return {
    workspace_id: workspaceId,
    workspace_name: workspaceName,
    user_id: userId,
    keyword_data: keywordData,
    isolation: {
      session_id: isolatedContext.isolatedContext.sessionId,
      processing_queue: isolatedContext.isolatedContext.processingQueue,
      validation_token: isolatedContext.isolatedContext.validationToken,
      request_id: isolatedContext.requestId,
      timestamp: isolatedContext.timestamp.toISOString()
    },
    security: {
      user_validation: 'required',
      workspace_validation: 'required',
      content_validation: 'required',
      isolation_level: 'strict'
    }
  };
}